import React, { useState } from 'react';
import { 
  GraduationCap, ArrowLeft, ArrowRight, ShieldCheck, Crown, 
  Satellite, Radio, Compass, Activity, Cpu, BookOpen, 
  FileText, Landmark, Award, Sparkles, Lock, Layers
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, getLocalizedAgentFeatures, Currency } from '../utils/i18n';
import PhdFullDissertationGenerator from './PhdFullDissertationGenerator';

interface PhdCenterHubViewProps {
  onLaunchAgent: (target: string) => void;
  onBack: () => void;
  language: 'uk' | 'en' | 'pl';
  currency?: Currency;
  currentUserEmail?: string;
}

export const PHD_AGENT_IDS = [
  'phd-eco-remote-sensing-analyst',
  'phd-skako-space-situational-guard',
  'phd-gnss-geodynamics-surveyor',
  'phd-special-control-geophysicist',
  'phd-data-fusion-math-modeler',
  'phd-dissertation-lead-orchestrator',
  'phd-global-trends-comparator',
  'phd-ukraine-capabilities-auditor',
  'phd-national-system-architect',
  'phd-academic-compliance-bibliographer'
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <GraduationCap className={className} />;
  return <IconComponent className={className} />;
};

export default function PhdCenterHubView({
  onLaunchAgent,
  onBack,
  language,
  currentUserEmail,
  currency = 'UAH'
}: PhdCenterHubViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const isAdmin = ['bbass776@gmail.com', 'irinacosmos777@gmail.com'].includes(currentUserEmail?.toLowerCase() || '');
  const [selectedSubTab, setSelectedSubTab] = useState<'generator' | 'all' | 'data' | 'fusion' | 'chapters' | 'admin'>('generator');

  if (!isAdmin) {
    return (
      <div className="p-8 text-center bg-slate-900 border border-red-500/30 rounded-2xl text-white font-mono space-y-4 max-w-xl mx-auto my-12 shadow-2xl">
        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto text-red-400">
          <Lock className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-bold text-red-400">
          {isPl ? '⛔ Dostęp ograniczony' : isUk ? '⛔ Доступ обмежено' : '⛔ Access Restricted'}
        </h2>
        <p className="text-sm text-slate-300">
          {isPl 
            ? 'Centrum Badań Doktorskich (Doktor Filozofii PhD) jest dostępne wyłącznie dla administratorów platformy.' 
            : isUk 
            ? 'Центр Наукових Досліджень та Дисертацій «Доктор Філософії (PhD)» доступний виключно для адміністраторів платформи.' 
            : 'The PhD Dissertation Research Center is restricted to platform administrators.'}
        </p>
        <button 
          onClick={onBack}
          className="px-5 py-2.5 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold uppercase rounded-xl transition-all cursor-pointer border border-slate-700 inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : '← Back to Main Hub'}</span>
        </button>
      </div>
    );
  }

  const allPhdAgents = agentsConfig.filter((a: any) => PHD_AGENT_IDS.includes(a.id));

  const filteredAgents = allPhdAgents.filter((agent: any) => {
    if (selectedSubTab === 'all') return true;
    if (selectedSubTab === 'data') {
      return [
        'phd-eco-remote-sensing-analyst',
        'phd-skako-space-situational-guard',
        'phd-gnss-geodynamics-surveyor',
        'phd-special-control-geophysicist'
      ].includes(agent.id);
    }
    if (selectedSubTab === 'fusion') {
      return agent.id === 'phd-data-fusion-math-modeler';
    }
    if (selectedSubTab === 'chapters') {
      return [
        'phd-dissertation-lead-orchestrator',
        'phd-global-trends-comparator',
        'phd-ukraine-capabilities-auditor',
        'phd-national-system-architect'
      ].includes(agent.id);
    }
    if (selectedSubTab === 'admin') {
      return agent.id === 'phd-academic-compliance-bibliographer';
    }
    return true;
  });

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-mono">
      {/* Header Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-purple-500/30 rounded-xl p-4 text-white shadow-lg">
        <button
          onClick={onBack}
          className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-purple-300 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-purple-500/40"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : '← Back to Main Hub'}</span>
        </button>

        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-purple-500/20 text-purple-300 border border-purple-400/40 rounded-md flex items-center gap-1">
            <GraduationCap className="w-3.5 h-3.5 text-purple-400" />
            {isPl ? '🎓 CENTRUM DOKTORA FILOZOFII (PHD)' : isUk ? '🎓 ЦЕНТР ДОКТОРА ФІЛОСОФІЇ (PHD)' : '🎓 PHD DISSERTATION CENTER'}
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-red-500/20 text-red-300 border border-red-400/30 rounded-md flex items-center gap-1">
            <Crown className="w-3 h-3 text-red-400" />
            ADMIN ONLY
          </span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-purple-950 to-indigo-950 border-2 border-purple-500/50 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute left-1/3 bottom-0 w-64 h-64 bg-blue-500/10 rounded-full blur-2xl pointer-events-none" />
        
        <div className="relative z-10 max-w-4xl space-y-3.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-purple-500/30 text-purple-300 border border-purple-400/40 rounded flex items-center gap-1">
              <span>🛰️</span>
              <span>ДЗЗ • СКАКО • ГНСС • СПЕЦКОНТРОЛЬ ГЦСК</span>
            </span>
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded">
              10 Наукових Агентів
            </span>
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-indigo-500/30 text-indigo-300 border border-indigo-400/30 rounded">
              ДСТУ 4163:2020 • НАЗЯВО • Scopus/WoS
            </span>
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight uppercase leading-tight bg-gradient-to-r from-white via-purple-100 to-indigo-300 bg-clip-text text-transparent">
            {isPl 
              ? 'CENTRUM NAUKOWE DOKTORA FILOZOFII (PHD SPACE-ECO HUB)' 
              : isUk 
              ? 'АКАДЕМІЧНИЙ ЦЕНТР ДИСЕРТАЦІЙ «ДОКТОР ФІЛОСОФІЇ (PHD)»' 
              : 'PHD DISSERTATION & SPACE-ECO RESEARCH CENTER'}
          </h1>

          <div className="text-xs sm:text-sm font-semibold text-purple-200 uppercase tracking-wide">
            Тема дослідження: «Космічна підтримка екологічного сектору України»
          </div>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-sans font-medium">
            {isPl 
              ? 'Kompleksowy system 10 modułów AI do przygotowania rozprawy doktorskiej (PhD) zgodnie ze standardami Ministerstwa Edukacji i Nauki oraz NAQA. Obejmuje analizę teledetekcji satelitarnej (EO/SAR), świadomość sytuacyjną w kosmosie (SKAKO/SSA), precyzyjne sieci GNSS, geofizyczny monitoring specjalny GCKS, modele fuzji danych (Data Fusion) oraz generowanie pełnych rozdziałów 1-4 z aparatem bibliograficznym.'
              : isUk 
              ? 'Інтегрована мультиагентна система з 10 ШІ-агентів для повного циклу написання та верифікації дисертації доктора філософії за вимогами МОН та НАЗЯВО (ПКМУ № 44). Покриває аналітику супутникового ДЗЗ (оптика/SAR), орбітальну безпеку СКАКО, мережі ГНСС, геофізичний спецконтроль ГЦСК ДКАУ, математичні моделі комплексування Data Fusion та генерацію розділів 1–4 у форматі Word (.docx за ДСТУ 4163:2020).'
              : 'Integrated multi-agent pipeline of 10 specialized AI agents for full-cycle PhD dissertation synthesis under national academic standards. Encompasses Earth Observation (optical/SAR), Space Situational Awareness (SSA/SKAKO), GNSS geodetic networks, Geophysical Special Control (GCKS), Data Fusion mathematical modeling, and complete Chapters 1-4 generation in Word (.docx).'}
          </p>
        </div>
      </div>

      {/* Subtab Filter */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-slate-800">
        <button
          onClick={() => setSelectedSubTab('generator')}
          className={`px-4 py-2.5 text-xs font-black uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
            selectedSubTab === 'generator'
              ? 'bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white shadow-lg shadow-purple-600/40 border border-purple-400'
              : 'bg-slate-900 text-purple-300 hover:bg-slate-800 border border-purple-500/40'
          }`}
        >
          <Sparkles className="w-4 h-4 text-amber-300 animate-pulse" />
          <span>{isPl ? '🚀 Generator Całej Rozprawy (1 Klik)' : isUk ? '🚀 Генератор Повної Дисертації у 1 Клік' : '🚀 1-Click Full Dissertation'}</span>
        </button>

        <button
          onClick={() => setSelectedSubTab('all')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'all'
              ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? 'Wszystkie Moduły' : isUk ? 'Усі Модулі' : 'All Modules'} ({allPhdAgents.length})
        </button>

        <button
          onClick={() => setSelectedSubTab('data')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            selectedSubTab === 'data'
              ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          <Satellite className="w-3.5 h-3.5" />
          <span>{isPl ? '1. Dane (EO/SSA/GNSS/GCKS)' : isUk ? '1. Дані (ДЗЗ/СКАКО/ГНСС/ГЦСК)' : '1. Domain Data'}</span>
        </button>

        <button
          onClick={() => setSelectedSubTab('fusion')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            selectedSubTab === 'fusion'
              ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          <Cpu className="w-3.5 h-3.5" />
          <span>{isPl ? '2. Data Fusion' : isUk ? '2. Data Fusion (Моделі)' : '2. Data Fusion'}</span>
        </button>

        <button
          onClick={() => setSelectedSubTab('chapters')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            selectedSubTab === 'chapters'
              ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          <BookOpen className="w-3.5 h-3.5" />
          <span>{isPl ? '3. Rozdziały 1-4' : isUk ? '3. Розділи Дисертації' : '3. Chapters 1-4'}</span>
        </button>

        <button
          onClick={() => setSelectedSubTab('admin')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            selectedSubTab === 'admin'
              ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          <Award className="w-3.5 h-3.5" />
          <span>{isPl ? '4. Bibliografia i Wnioski' : isUk ? '4. Бібліографія & Висновки' : '4. Bibliography'}</span>
        </button>
      </div>

      {/* Main Content Area */}
      {selectedSubTab === 'generator' ? (
        <PhdFullDissertationGenerator
          language={language}
          onLaunchSpecificAgent={onLaunchAgent}
        />
      ) : (
        /* Agents Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredAgents.map((agent: any) => (
          <div
            key={agent.id}
            className="bg-slate-900 border border-purple-500/30 hover:border-purple-400/70 rounded-2xl p-6 text-white shadow-xl transition-all flex flex-col justify-between space-y-4 hover:shadow-purple-500/10 group"
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-purple-500/20 text-purple-300 border border-purple-400/30 rounded-md flex items-center gap-1.5">
                  <AgentIcon name={agent.icon} className="w-3.5 h-3.5 text-purple-400" />
                  {getLocalizedAgentCategory(agent, language)}
                </span>
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-amber-500/20 text-amber-300 border border-amber-400/30 rounded flex items-center gap-1">
                  <Crown className="w-3 h-3 text-amber-400" />
                  ADMIN ONLY
                </span>
              </div>

              <h3 className="text-lg font-extrabold text-purple-100 group-hover:text-purple-200 uppercase tracking-tight flex items-center gap-2">
                <span>{getLocalizedAgentName(agent, language)}</span>
              </h3>

              <p className="text-xs text-slate-300 leading-relaxed font-sans">
                {getLocalizedAgentDesc(agent, language)}
              </p>

              <div className="space-y-1.5 pt-2">
                <span className="text-[10px] font-extrabold text-purple-300 uppercase tracking-wider">
                  {isPl ? 'Główne zadania i rezultaty:' : isUk ? 'Ключові наукові завдання та результати:' : 'Key Scientific Outcomes:'}
                </span>
                <ul className="space-y-1">
                  {getLocalizedAgentFeatures(agent, language).map((feature: string, idx: number) => (
                    <li key={idx} className="text-xs text-slate-300 flex items-start gap-2">
                      <span className="text-purple-400 text-xs font-bold mt-0.5">•</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-800 flex items-center justify-between gap-4">
              <div className="text-[11px] text-slate-400">
                <span className="font-bold text-purple-300">{isPl ? 'Model:' : isUk ? 'Модель:' : 'Model:'}</span> {agent.model || 'gemini-3.7-flash'}
              </div>
              <button
                onClick={() => onLaunchAgent(agent.target || agent.id)}
                className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-md shadow-purple-600/30 flex items-center gap-2 cursor-pointer hover:scale-[1.02]"
              >
                <span>{isPl ? '🚀 Otwórz Moduł Badawczy' : isUk ? '🚀 Відкрити Науковий Модуль' : '🚀 Open Research Module'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        </div>
      )}
    </div>
  );
}
